// Cache tags for the generated tenants endpoints, kept separate from the api wiring in
// `tenantsEnhanced.ts` so the policy can be read and tested without pulling in the whole store.
// Deliberately import-free.

const TENANTS_LIST = { type: 'Tenants' as const, id: 'LIST' }
const TENANT_NOTES_LIST = { type: 'TenantNotes' as const, id: 'LIST' }
// The theme has no useful id: one row per tenant, and the sidebar logo, branding screen and
// logo dropzone all read the same record.
const TENANT_THEME = { type: 'TenantTheme' as const }
const EMPLOYEE_ID_CONFIG = { type: 'EmployeeIdConfig' as const }

// The tenants endpoint answers paginated ({ items }) or as a plain array depending on its
// params; both shapes get per-row tags. The guards also keep error payloads out of the mapper.
const providesTenantsList = (result: { items?: { id: string }[] } | { id: string }[] | undefined) => {
  if (!result) return [TENANTS_LIST]

  if ('items' in result && Array.isArray(result.items)) {
    return [...result.items.map(({ id }) => ({ type: 'Tenants' as const, id })), TENANTS_LIST]
  }

  if (Array.isArray(result)) {
    return [...result.map(({ id }) => ({ type: 'Tenants' as const, id })), TENANTS_LIST]
  }

  return [TENANTS_LIST]
}

const providesNotesList = (result: { id: string }[] | undefined) =>
  Array.isArray(result)
    ? [...result.map(({ id }) => ({ type: 'TenantNotes' as const, id })), TENANT_NOTES_LIST]
    : [TENANT_NOTES_LIST]

export const tenantsCacheTags = {
  listTenants: {
    providesTags: providesTenantsList,
  },
  createTenant: {
    invalidatesTags: [TENANTS_LIST],
  },
  deleteTenant: {
    invalidatesTags: [TENANTS_LIST],
  },
  updateTenant: {
    invalidatesTags: [TENANTS_LIST],
  },
  listTenantNotes: {
    providesTags: providesNotesList,
  },
  createTenantNote: {
    invalidatesTags: [TENANT_NOTES_LIST],
  },
  deleteTenantNote: {
    invalidatesTags: [TENANT_NOTES_LIST],
  },
  updateTenantNote: {
    invalidatesTags: [TENANT_NOTES_LIST],
  },
  deleteAllTenantNotes: {
    invalidatesTags: [TENANT_NOTES_LIST],
  },
  getTenantTheme: {
    providesTags: [TENANT_THEME],
  },
  upsertTenantTheme: {
    invalidatesTags: [TENANT_THEME],
  },
  upsertTenantLogo: {
    invalidatesTags: [TENANT_THEME],
  },
  deleteTenantLogo: {
    invalidatesTags: [TENANT_THEME],
  },
  getEmployeeIdConfig: {
    providesTags: [EMPLOYEE_ID_CONFIG],
  },
  upsertEmployeeIdConfig: {
    invalidatesTags: [EMPLOYEE_ID_CONFIG],
  },
} as const
